import { useState } from 'react';

import { useQuery } from '@apollo/client';

import { lienholdersQuery } from '../gql/prs/lienholderGql';
import { LienholderPRSType } from '../gql/prs/types';

import { Option } from '../components/shared/types';
import { OtherLienholderSlug } from '../constants/lienholders';
import { mapPRSLienholdersToOptions } from '../utils/prs';

interface Props {
  state?: string;
  make?: string;
}

export const useLienholders = ({ state = '', make = '' }: Props = {}) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [probableOptions, setProbableOptions] = useState<Option[]>([]);

  const { loading } = useQuery<{ lienholders: LienholderPRSType[] }>(lienholdersQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      // Default state to get initial lienholders. Could be any state.
      state: state || 'UT',
      make,
    },
    onCompleted: ({ lienholders }) => {
      if (!lienholders?.length) {
        setOptions([]);
        setProbableOptions([]);
        return;
      }

      // Reps only pick from core lienholders (+ literal Other, which is non-core in
      // PRS); non-core records exist for the customer flow and render as Other here.
      const coreLienholders = lienholders
        .filter((lienholder) => lienholder.is_core || lienholder.slug === OtherLienholderSlug)
        .sort((a, b) => a.slug.localeCompare(b.slug));
      setOptions(mapPRSLienholdersToOptions(coreLienholders));

      const probableLienholders = coreLienholders.filter(
        (lienholder) => lienholder.probableLienholder || lienholder.slug === OtherLienholderSlug,
      );
      setProbableOptions(mapPRSLienholdersToOptions(probableLienholders));
    },
    onError: () => {
      setOptions([]);
      setProbableOptions([]);
    },
  });

  return {
    loading,
    options,
    probableOptions,
  };
};
